<template>
  <div class="riw fm" :class="{ 'fm--schema': schemaUpdateMode }">
    <!-- Preview (rail Config / Preview switch): every parsed row with the current mapping applied -->
    <import-preview-view
      v-if="isPreview"
      :rows="excelData"
      :issues="rowEvaluation.issues"
      :counts="rowEvaluation.counts"
      :columns="previewColumns"
      :facility-applies="facilityApplies"
      :alias-target-of="aliasTargetOf"
      :identity-label="mappingStatus.identity"
      :facility-filter="previewFacilityFilter" />

    <template v-else>
      <!-- Settings (Tag column · Facility column | Load template · Save mapping) + Facility name mapping.
           Collapsible sections, all open by default; a collapsed header keeps a one-line summary. -->
      <div v-if="!schemaUpdateMode" class="fm__top" :class="{ 'fm__top--collapsed': topCollapsed }">
        <section :aria-labelledby="sectionIds.settings.toggle">
          <div class="riw-acc-head">
            <import-section-toggle
              :id="sectionIds.settings.toggle"
              ref="settingsToggle"
              :open="sections.settings"
              label="Settings"
              :controls="sectionIds.settings.body"
              @toggle="toggleSection('settings')" />
            <span v-if="!sections.settings" class="riw-acc-sum">
              <span :class="{ 'is-muted': !selectedTagColumn }">Tag <b>{{ selectedTagColumn || 'not set' }}</b></span>
              <template v-if="facilityColumnEnabled && !facilityColumnMissing">
                <span class="riw-acc-sum__sep">·</span>
                <span :class="{ 'is-muted': !facilityColumn }">Facility <b>{{ facilityColumn || 'not set' }}</b></span>
              </template>
              <template v-if="selectedTemplateName">
                <span class="riw-acc-sum__sep">·</span>
                <span>Template <b>{{ selectedTemplateName }}</b></span>
              </template>
            </span>
            <span v-if="!sections.settings && facilityColumnMissing" class="riw-pill riw-pill--err">
              <v-icon>mdi-alert-circle-outline</v-icon>Facility column required
            </span>
          </div>

          <div
            :id="sectionIds.settings.body"
            class="fm__settings riw-acc-body"
            role="group"
            aria-label="Key columns and mapping templates"
            :hidden="!sections.settings">
            <div class="fm__setting">
              <div class="fm__label">
                <v-icon size="15">mdi-tag-outline</v-icon>Tag column
                <span class="riw-badge riw-badge--opt">Recommended</span>
              </div>
              <v-select
                v-model="selectedTagColumn"
                :items="filteredExcelColumns"
                class="riw-input"
                placeholder="Select column…"
                aria-label="Tag column"
                prepend-inner-icon="mdi-tag-outline"
                dense outlined hide-details clearable
                @change="onTagColumnChange" />
            </div>
            <div class="fm__setting">
              <div class="fm__label">
                <v-icon size="15">mdi-domain</v-icon>Facility column
                <!-- The long explanation lives here (hover or keyboard focus); the tooltip is anchored
                     to the whole cell so it never runs past the dialog edge. -->
                <button
                  v-if="facilityPresentation.tip"
                  type="button"
                  class="riw-info-tip"
                  :data-tip="facilityPresentation.tip"
                  aria-label="About the Facility column"
                  :aria-describedby="`${_uid}-fac-help`">
                  <v-icon size="14">mdi-information-outline</v-icon>
                </button>
                <span
                  class="riw-badge"
                  :class="facilityPresentation.badge.cls"
                  :title="facilityPresentation.badge.title || null">
                  <v-icon v-if="facilityPresentation.badge.icon" size="12">{{ facilityPresentation.badge.icon }}</v-icon>{{ facilityPresentation.badge.text }}
                </span>
              </div>
              <span v-if="facilityPresentation.tip" :id="`${_uid}-fac-help`" class="riw-sr-only">{{ facilityPresentation.tip }}</span>
              <v-select
                v-if="facilityColumnEnabled"
                v-model="facilityColumn"
                :items="filteredExcelColumns"
                class="riw-input"
                :class="{ 'riw-input--required': facilityColumnMissing }"
                placeholder="Select column…"
                aria-label="Facility column"
                :aria-describedby="facilityDescribedBy"
                prepend-inner-icon="mdi-domain"
                :clearable="registerKind !== REGISTER_KIND.GROUP_SEPARATED"
                dense outlined hide-details
                @change="onFacilityColumnChange" />
              <v-select
                v-else
                :value="null"
                :items="[]"
                class="riw-input"
                placeholder="Not used"
                aria-label="Facility column"
                :aria-describedby="facilityDescribedBy"
                prepend-inner-icon="mdi-domain"
                append-icon="mdi-lock-outline"
                disabled dense outlined hide-details />
              <div
                v-if="facilityPresentation.cap"
                :id="`${_uid}-fac-cap`"
                class="riw-field-cap riw-field-cap--reason riw-field-cap--one-line"
                :class="{ 'riw-field-cap--req': facilityPresentation.cap.required }"
                :title="facilityPresentation.cap.text">
                <v-icon>{{ facilityPresentation.cap.icon }}</v-icon><span>{{ facilityPresentation.cap.text }}</span>
              </div>
            </div>

            <span class="fm__rule" aria-hidden="true"></span>

            <div class="fm__setting">
              <div class="fm__label"><v-icon size="15">mdi-file-table-box-outline</v-icon>Load a template</div>
              <div class="fm__control">
                <v-select
                  v-model="selectedTemplateId"
                  :items="templateOptions"
                  class="riw-input"
                  placeholder="Select template…"
                  aria-label="Load a template"
                  no-data-text="No saved templates"
                  dense outlined hide-details clearable
                  @change="onTemplateSelected">
                  <template v-slot:item="{ item }">
                    <div class="fm__tpl-item">
                      <span class="fm__tpl-name">{{ item.text }}</span>
                      <v-btn
                        icon
                        x-small
                        class="fm__tpl-del"
                        :aria-label="`Delete template ${item.text}`"
                        title="Delete template"
                        @click.stop="deleteTemplate(item.value, item.text)">
                        <v-icon size="16">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-select>
                <v-btn
                  small outlined color="primary"
                  class="riw-square-btn"
                  aria-label="Load template"
                  title="Load template"
                  :disabled="!selectedTemplateId || !selectedTemplateData"
                  @click="loadSelectedTemplate">
                  <v-icon size="17">mdi-download</v-icon>
                </v-btn>
              </div>
            </div>
            <div class="fm__setting">
              <div class="fm__label"><v-icon size="15">mdi-content-save-outline</v-icon>Save current mapping</div>
              <div class="fm__control">
                <v-text-field
                  v-model="newTemplateName"
                  class="riw-input"
                  placeholder="Template name"
                  aria-label="Template name"
                  dense outlined hide-details
                  @keydown.enter.prevent="saveCurrentTemplate" />
                <v-btn
                  small outlined color="primary"
                  class="riw-square-btn"
                  aria-label="Save template"
                  title="Save template"
                  :disabled="!canSaveTemplate"
                  @click="saveCurrentTemplate">
                  <v-icon size="17">mdi-content-save</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </section>

        <facility-value-mapping-editor
          v-if="facilityApplies"
          ref="facilityEditor"
          v-model="facilityValueMappings"
          :open="sections.facility"
          :members="members"
          :loading="membersLoading"
          :load-error="membersError"
          :file-values="facilityFileValues"
          :column-label="facilityColumn"
          :group-name="facilityGroupName"
          @update:open="setSection('facility', $event)"
          @input="emitMappingComplete"
          @validity="facilityAliasesValid = $event" />
      </div>

      <!-- Compact one-line alerts: saved-schema banner, mode notes, blockers -->
      <div v-if="alerts.length" class="fm__alerts riw-callout-stack">
        <div
          v-for="alert in alerts"
          :key="alert.key"
          class="riw-callout riw-callout--compact"
          :class="[`riw-callout--${alert.kind}`, { 'riw-callout--one-line': alert.oneLine }]"
          :role="alert.role || (alert.register ? 'alert' : null)">
          <v-icon class="riw-callout__icon">{{ alert.icon }}</v-icon>
          <div class="riw-callout__body"><span class="riw-callout__title">{{ alert.title }}</span>{{ alert.text }}</div>
          <div v-if="alert.actions === true" class="riw-callout__actions">
            <v-btn small text @click="dismissSavedSchema">Not now</v-btn>
            <v-btn small depressed color="primary" @click="loadSavedSchema">
              <v-icon left small>mdi-download</v-icon>Load saved schema
            </v-btn>
          </div>
          <!-- Host-driven actions (API source identity guidance): [{ key, label, icon?, outlined? }] -->
          <div v-else-if="Array.isArray(alert.actions) && alert.actions.length" class="riw-callout__actions">
            <v-btn
              v-for="action in alert.actions"
              :key="action.key"
              small
              :outlined="!!action.outlined"
              :text="!action.outlined"
              class="riw-callout__btn"
              @click="onAlertAction(action.key)">
              <v-icon v-if="action.icon" left small>{{ action.icon }}</v-icon>{{ action.label }}
            </v-btn>
          </div>
          <!-- The error's section is collapsed: offer to open it (focus moves to its header). -->
          <div v-else-if="alert.section && !sections[alert.section]" class="riw-callout__actions">
            <v-btn small text @click="showSection(alert.section)">
              <v-icon left small>mdi-chevron-down</v-icon>Show
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Table card: only the table scrolls. Its toolbar is the Column mapping section header. -->
      <div class="fm__table-wrap" :class="{ 'is-collapsed': columnsCollapsed }">
        <div class="riw-table-card">
          <div class="fm__toolbar">
            <import-section-toggle
              v-if="!schemaUpdateMode"
              :id="sectionIds.columns.toggle"
              ref="columnsToggle"
              :open="sections.columns"
              label="Column mapping"
              :controls="sectionIds.columns.body"
              @toggle="toggleSection('columns')" />
            <div class="fm__counts">
              <span class="fm__count"><b>{{ mappingStatus.totalColumns }}</b>{{ schemaUpdateMode ? 'schema fields' : `${sourceLabel} columns` }}</span>
              <span class="fm__count"><b>{{ mappedColumnsCount }}</b>configured</span>
              <span class="fm__count"><b>{{ Object.keys(mandatoryFieldMappings).length }}</b>mandatory</span>
              <span v-if="linkDesignationEnabled" class="fm__count"><b>{{ linkedColumnsCount }}</b>linked</span>
            </div>
            <span v-if="columnsChip" class="riw-pill" :class="`riw-pill--${columnsChip.tone}`">
              <v-icon>{{ columnsChip.icon }}</v-icon>{{ columnsChip.text }}
            </span>
            <span v-if="!schemaUpdateMode" class="fm__actions">
              <v-btn small text class="fm__clear" :disabled="!hasAnyMappings" @click="resetFieldNames">
                <v-icon left small>mdi-close-circle-outline</v-icon>Clear All
              </v-btn>
              <v-btn small outlined color="primary" :disabled="excelColumns.length === 0" @click="autoSuggestFieldNames">
                <v-icon left small>mdi-auto-fix</v-icon>Auto Fill
              </v-btn>
            </span>
          </div>

          <div
            :id="sectionIds.columns.body"
            class="riw-table-scroll"
            :hidden="columnsCollapsed">
            <table class="fm__table" :class="{ 'fm__table--no-links': !linkDesignationEnabled }">
              <thead>
                <tr>
                  <th>{{ schemaUpdateMode ? 'Schema field' : `${sourceLabel} column` }}</th>
                  <th class="fm__arrow" aria-hidden="true"></th>
                  <th class="fm__col-name">Field name</th>
                  <th class="fm__col-type">Type</th>
                  <th class="fm__col-mand is-center">Mandatory</th>
                  <th class="fm__col-id is-center" :class="{ 'fm__col-id--required': identityRequired }">
                    Identity<span
                      v-if="identityRequired"
                      class="fm__req"
                      role="img"
                      aria-label="Required for scheduled sources"
                      title="Required for scheduled sources">*</span>
                  </th>
                  <th v-if="linkDesignationEnabled" class="fm__col-link">Links to</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="column in rowColumns">
                  <tr :key="`r-${column}`" class="fm__row" :class="rowClass(column)">
                    <td>
                      <div class="fm__src">
                        <v-icon size="15" class="fm__src-status">{{ rowStatusIcon(column) }}</v-icon>
                        <div class="fm__src-meta">
                          <span class="fm__src-name" :title="column">{{ column }}</span>
                          <span class="fm__src-sample riw-mono" :title="schemaUpdateMode ? null : getColumnPreview(column)">
                            {{ schemaUpdateMode ? 'Schema field' : getColumnPreview(column) }}
                          </span>
                        </div>
                        <span v-if="!schemaUpdateMode && column === selectedTagColumn" class="riw-pill riw-pill--tag"><v-icon>mdi-tag</v-icon>Tag</span>
                        <span v-if="!schemaUpdateMode && column === facilityColumn" class="riw-pill riw-pill--facility"><v-icon>mdi-domain</v-icon>Facility</span>
                      </div>
                    </td>
                    <td class="fm__arrow"><v-icon size="18">mdi-arrow-right</v-icon></td>
                    <td>
                      <v-text-field
                        v-model="customFields[column]"
                        class="riw-input"
                        :placeholder="fieldPlaceholder(column)"
                        :aria-label="`Field name for ${column}`"
                        prepend-inner-icon="mdi-form-textbox"
                        dense outlined hide-details
                        @input="onFieldNameChange(column, $event)">
                        <template v-if="customFields[column] && !schemaUpdateMode" v-slot:append>
                          <v-btn icon x-small aria-label="Clear field name" title="Clear" @click="clearFieldName(column)">
                            <v-icon size="14">mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </td>
                    <td>
                      <v-select
                        v-if="isColumnConfigured(column)"
                        v-model="fieldTypes[column]"
                        :items="FIELD_TYPE_OPTIONS"
                        class="riw-input"
                        :aria-label="`Type for ${column}`"
                        :disabled="schemaUpdateMode"
                        :append-icon="schemaUpdateMode ? 'mdi-lock-outline' : '$dropdown'"
                        :title="schemaUpdateMode ? 'Locked — the register already holds data' : null"
                        dense outlined hide-details
                        @change="onFieldTypeChange(column)" />
                      <span v-else class="riw-dash">—</span>
                    </td>
                    <td class="is-center">
                      <label v-if="isColumnConfigured(column)" class="fm__check">
                        <input
                          type="checkbox"
                          :checked="!!mandatoryFields[column]"
                          :disabled="schemaUpdateMode"
                          :aria-label="`Mandatory ${column}`"
                          @change="setMandatory(column, $event.target.checked)" />
                      </label>
                      <span v-else class="riw-dash">—</span>
                    </td>
                    <td class="is-center">
                      <label
                        v-if="isColumnConfigured(column)"
                        class="fm__radio"
                        :class="{ 'is-on': identityColumn === column }"
                        title="Identity column — used to update existing rows on re-import">
                        <input
                          type="radio"
                          :name="`${_uid}-identity`"
                          :checked="identityColumn === column"
                          :disabled="schemaUpdateMode"
                          :aria-label="`Identity ${column}`"
                          @change="setIdentity(column)" />
                        <span v-if="identityRequired && !hasIdentity && column === identitySuggestion" class="fm__id-hint">Suggested</span>
                      </label>
                      <span v-else class="riw-dash">—</span>
                    </td>
                    <td v-if="linkDesignationEnabled">
                      <v-select
                        v-if="isColumnConfigured(column) && columnLink(column)"
                        :value="columnLink(column).linkEntityType"
                        :items="LINK_ENTITY_TYPE_OPTIONS"
                        class="riw-input"
                        :aria-label="`Links to for ${column}`"
                        prepend-inner-icon="mdi-link-variant"
                        dense outlined hide-details
                        @change="onLinkEntityTypeChange(column, $event)" />
                      <span v-else class="riw-dash">—</span>
                    </td>
                  </tr>
                  <tr
                    v-if="linkDesignationEnabled && isColumnConfigured(column) && columnLink(column) && columnLink(column).linkEntityType"
                    :key="`l-${column}`"
                    class="fm__link-row">
                    <td :colspan="7">
                      <div class="fm__link-sub">
                        <span class="fm__link-lbl"><v-icon size="15">mdi-subdirectory-arrow-right</v-icon>{{ linkTypeLabel(column) }} link</span>
                        <span class="fm__link-lbl">Match on</span>
                        <v-select
                          :value="columnLink(column).linkKeyField"
                          :items="linkKeyFieldOptions(column)"
                          class="riw-input fm__link-field"
                          aria-label="Match on"
                          dense outlined hide-details
                          @change="onLinkKeyFieldChange(column, $event)" />
                        <span class="fm__link-lbl">Register default</span>
                        <v-select
                          :value="linkDefaultRole(column)"
                          :items="linkDefaultRoleOptions(column)"
                          class="riw-input fm__link-field fm__link-field--wide"
                          aria-label="Register default"
                          dense outlined hide-details
                          @change="onLinkDefaultRoleChange(column, $event)" />
                        <span v-if="linkDefaultRole(column) !== 'none'" class="riw-pill riw-pill--alias">
                          <v-icon>{{ linkDefaultRole(column) === 'highlight' ? 'mdi-target' : 'mdi-image-filter-center-focus-weak' }}</v-icon>
                          {{ linkDefaultRole(column) === 'highlight' ? 'Highlight' : 'Context' }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!rowColumns.length">
                  <td :colspan="linkDesignationEnabled ? 7 : 6" class="fm__empty">No columns to map.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { FIELD_TYPE_OPTIONS, FIELD_TYPES } from '@/enums/columnMappings.js';
  import { mapGetters, mapActions } from 'vuex';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
  import groupsService from '@Groups/service/groups.service';
  import registerService from '../../services/registerService';
  import FacilityValueMappingEditor from './FacilityValueMappingEditor.vue';
  import ImportPreviewView from './importWizard/ImportPreviewView.vue';
  import ImportSectionToggle from './importWizard/ImportSectionToggle.vue';
  import { buildFacilityFilterModel } from './importWizard/facilityPreviewFilter';
  import './importWizard/styles';
  import { copyFacilityAliases } from '../../modules/registerImportContracts';
  import {
    FACILITY_MATCH,
    REGISTER_KIND,
    buildPreviewContext,
    evaluateRows,
    facilityColumnAllowed,
    resolveFacilityValue,
    resolveRegisterKind,
    resultingMode,
    summarizeColumnValues,
  } from '../../modules/registerImportWizardModel';
  import { apiIdentityAlert, suggestIdentityColumn } from '../../modules/apiSourceRules';
  import {
    MAP_SECTION,
    errorSections,
    facilityColumnPresentation,
    mappingAlerts,
    mappingBlockers,
    newlyFailingSections,
  } from './importWizard/fieldMappingPresentation';
  import {
    LINK_ENTITY_TYPE_OPTIONS,
    canContext,
    canHighlight,
    defaultKeyFieldFor,
    defaultRoleOptions,
    flagsFromRole,
    keyFieldOptions,
    roleFromFlags,
  } from '../../modules/registerLinkOptions';

  export default {
    name: 'FieldMapping',
    components: {
      FacilityValueMappingEditor,
      ImportPreviewView,
      ImportSectionToggle,
    },
    props: {
      initialMapping: { type: Object, default: null },
      excelColumns: {
        type: Array,
        default: () => [],
      },
      excelData: {
        type: Array,
        default: () => [],
      },
      schemaUpdateMode: {
        type: Boolean,
        default: false,
      },
      register: {
        type: Object,
        default: null,
      },
      sourceLabel: {
        type: String,
        default: 'Excel',
      },
      /**
       * Kept for compatibility (the API data-source wizard passes it). The mapping step no longer
       * renders its own action bar: the host wizard owns the footer, reads `status`, and calls
       * `saveMappings()`.
       */
      hideActions: {
        type: Boolean,
        default: false,
      },
      /** 'config' (mapping table) or 'preview' (in-place data preview) — the rail view switch. */
      view: {
        type: String,
        default: 'config',
      },
      /**
       * Stored row count of the register, when the host knows it. Decides whether an unset Facility
       * Group register is empty (first import sets the mode) or populated (import refused, LEG-5).
       * null = unknown → treated as empty; the server still refuses a populated register.
       */
      registerRowCount: {
        type: Number,
        default: null,
      },
      /**
       * Mapping built from the register's saved schema (ExcelUpload.buildMappingFromRegisterSchema).
       * When set, a compact banner offers to load it — replacing the old "Use Saved Schema?" dialog.
       */
      savedSchemaMapping: {
        type: Object,
        default: null,
      },
      /**
       * Opt this mapping step into the link designation ("Links to") controls when it is NOT the
       * schema-update dialog. Set by the Excel import flow, which persists the designations after
       * the import (see ExcelUpload.applyImportColumnLinks). Left false for the API data-source
       * wizard, which forwards the mapping payload to its own endpoint and has no channel to save
       * a designation — showing the controls there would be a picker that silently does nothing.
       */
      allowLinkDesignation: {
        type: Boolean,
        default: false,
      },
      /**
       * API data-source wizard only: the source's current schedule frequency (0 = Manual Only). When
       * set, the generic identity warning gives way to schedule-aware guidance ("Use <column>",
       * "Switch to Manual Only"). null (the Excel import) keeps the generic warning.
       */
      scheduleFrequency: {
        type: Number,
        default: null,
      },
      /** Marks the Identity header with a red asterisk ("Required for scheduled sources"). */
      identityRequired: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        FIELD_TYPE_OPTIONS,
        LINK_ENTITY_TYPE_OPTIONS,
        customFields: {},
        fieldNameErrors: {},
        mandatoryFields: {}, // Track which fields are mandatory
        fieldTypes: {}, // Track field types per column
        // Link designation per column, keyed like customFields (originalName). Each entry carries
        // the column's INTERNAL name — the identity the link endpoint matches on.
        columnLinks: {},
        // Snapshot taken at hydrate time; only columns that differ from it are PUT on save. It
        // also carries each column's STORED display name, which is what ExcelUpload compares the
        // saved field names against to spot a renamed designated column (a rename changes the
        // server's resolution config even though no designation was touched).
        columnLinksBaseline: {},
        isProcessing: false,
        selectedTagColumn: null,
        facilityColumn: null,
        facilityValueMappings: [],
        facilityAliasesValid: true,
        identityColumn: '',
        selectedTemplateId: null,
        selectedTemplateData: null,
        newTemplateName: '',
        builtinTemplates: [],
        // Current member Facilities of the Facility Group — for the alias editor AND the preview's
        // Facility skip prediction, so both resolve values with the same list.
        members: [],
        membersLoading: false,
        membersError: '',
        membersGeneration: 0,
        savedSchemaHandled: false,
        REGISTER_KIND,
        // Map Fields accordion — all open by default. Component state only (resets per wizard).
        sections: { [MAP_SECTION.SETTINGS]: true, [MAP_SECTION.FACILITY]: true, [MAP_SECTION.COLUMNS]: true },
        // Sections with a blocking error at the last check: only a NEW error re-opens its section.
        lastErrorSections: [],
        // Facility column dropped from a loaded mapping because the register is Shared (null = none,
        // '' = only its Facility name mappings were dropped). Shown as an inline note.
        droppedFacilityColumn: null,
      };
    },
    computed: {
      registerKind() {
        return this.schemaUpdateMode ? null : resolveRegisterKind(this.register, this.registerRowCount);
      },
      facilityColumnEnabled() {
        return facilityColumnAllowed(this.registerKind);
      },
      /** Rows are assigned by Facility (Separated, or a first import with a Facility column). */
      facilityApplies() {
        return this.isFacilityGroupImport && this.facilityColumnEnabled && !!this.facilityColumn;
      },
      facilityColumnMissing() {
        return this.registerKind === REGISTER_KIND.GROUP_SEPARATED && !this.facilityColumn;
      },
      facilityPresentation() {
        return facilityColumnPresentation(this.registerKind, {
          hasFacilityColumn: !!this.facilityColumn,
          facilityName: this.register && this.register.facility && this.register.facility.name,
          groupName: this.facilityGroupName,
        });
      },
      /** The Facility select is described by the tooltip text and, when shown, the one-line cap. */
      facilityDescribedBy() {
        const ids = [];
        if (this.facilityPresentation.tip) ids.push(`${this._uid}-fac-help`);
        if (this.facilityPresentation.cap) ids.push(`${this._uid}-fac-cap`);
        return ids.length ? ids.join(' ') : null;
      },
      /** DOM ids of each section's header button and body (unique per FieldMapping instance). */
      sectionIds() {
        const id = (key, part) => `fm-${this._uid}-${key}-${part}`;
        return Object.values(MAP_SECTION).reduce((ids, key) => ({
          ...ids,
          [key]: { toggle: id(key, 'toggle'), body: id(key, 'body') },
        }), {});
      },
      /** Settings and (when shown) Facility name mapping both collapsed: the top block tightens. */
      topCollapsed() {
        return !this.sections.settings && (!this.facilityApplies || !this.sections.facility);
      },
      columnsCollapsed() {
        return !this.schemaUpdateMode && !this.sections.columns;
      },
      /** Collapsed Column mapping header: keeps a blocking error (red) or the identity warning visible. */
      columnsChip() {
        if (!this.columnsCollapsed) return null;
        if (this.blockers.some((b) => b.kind === 'err' && b.section === MAP_SECTION.COLUMNS)) {
          return { tone: 'err', icon: 'mdi-alert-circle-outline', text: 'No fields configured' };
        }
        const hasIdentity = !!this.identityColumn && this.isColumnConfigured(this.identityColumn);
        if (this.mappedColumnsCount > 0 && !hasIdentity) {
          return { tone: 'warn', icon: 'mdi-fingerprint-off', text: 'No identity column' };
        }
        return null;
      },
      selectedTemplateName() {
        const option = this.templateOptions.find((item) => item.value === this.selectedTemplateId);
        return option ? option.text : '';
      },
      facilityGroupName() {
        return (this.register && this.register.facilityGroup && this.register.facilityGroup.name) || '';
      },
      facilityFileValues() {
        return this.facilityApplies ? summarizeColumnValues(this.excelData, this.facilityColumn) : [];
      },
      isPreview() {
        return this.view === 'preview' && !this.schemaUpdateMode;
      },
      /** One table row per source column (import) or stored schema field (schema update). */
      rowColumns() {
        return this.schemaUpdateMode ? Object.keys(this.customFields) : this.filteredExcelColumns;
      },
      showSavedBanner() {
        return !this.schemaUpdateMode && !!this.savedSchemaMapping && !this.savedSchemaHandled;
      },
      /**
       * Facility resolution context for the preview: only once the Facility Group's Facilities are
       * loaded — resolving against an empty member list would make every row "not matched".
       */
      previewFacilityContext() {
        return this.facilityApplies && !this.membersLoading && !this.membersError
          ? { column: this.facilityColumn, aliases: this.facilityValueMappings, members: this.members }
          : null;
      },
      /** Preview Facility filter: rows grouped by the SAME resolution as the Facility skip prediction. */
      previewFacilityFilter() {
        if (!this.isPreview || !this.previewFacilityContext) return null;
        return buildFacilityFilterModel(this.excelData, this.previewFacilityContext);
      },
      /** Server-parity outcome of every parsed row with the current mapping (preview + footer). */
      rowEvaluation() {
        const facility = this.previewFacilityContext;
        const ctx = buildPreviewContext(this.excelData, {
          mandatoryFields: this.mandatoryFieldMappings,
          identityColumn: this.isColumnConfigured(this.identityColumn) ? this.identityColumn : null,
        }, facility);
        return evaluateRows(this.excelData, ctx);
      },
      previewColumns() {
        const typeText = (column) => {
          const option = FIELD_TYPE_OPTIONS.find((o) => o.value === this.fieldTypes[column]);
          const link = this.columnLinks[column] && this.columnLinks[column].linkEntityType;
          const linkText = link ? ` → ${(LINK_ENTITY_TYPE_OPTIONS.find((o) => o.value === link) || {}).text || link}` : '';
          return `${option ? option.text : 'Text'}${linkText}`;
        };
        const columns = [];
        const used = new Set();
        if (this.identityColumn && this.isColumnConfigured(this.identityColumn)) {
          columns.push({ key: this.identityColumn, label: this.customFields[this.identityColumn], type: typeText(this.identityColumn), identity: true });
          used.add(this.identityColumn);
        }
        if (this.selectedTagColumn && !used.has(this.selectedTagColumn)) {
          columns.push({ key: this.selectedTagColumn, label: 'Tag', type: 'Tag' });
          used.add(this.selectedTagColumn);
        }
        if (this.facilityApplies && !used.has(this.facilityColumn)) {
          columns.push({ key: this.facilityColumn, label: 'Facility', type: `Column “${this.facilityColumn}”`, facility: true });
          used.add(this.facilityColumn);
        }
        this.filteredExcelColumns.forEach((column) => {
          if (used.has(column) || !this.isColumnConfigured(column)) return;
          columns.push({
            key: column,
            label: this.customFields[column],
            type: typeText(column),
            numeric: this.fieldTypes[column] === FIELD_TYPES.NUMBER,
          });
        });
        return columns;
      },
      blockers() {
        return mappingBlockers({
          schemaUpdateMode: this.schemaUpdateMode,
          registerKind: this.registerKind,
          registerRowCount: this.registerRowCount,
          facilityColumnMissing: this.facilityColumnMissing,
          facilityApplies: this.facilityApplies,
          membersLoading: this.membersLoading,
          membersError: this.membersError,
          facilityAliasesValid: this.facilityAliasesValid,
          facilityMappingReady: this.facilityMappingReady,
          mappedColumnsCount: this.mappedColumnsCount,
          hasIdentity: this.hasIdentity,
          identityGuidance: this.hasIdentityGuidance,
          sourceLabel: this.sourceLabel,
          counts: this.rowEvaluation.counts,
        });
      },
      alerts() {
        const list = mappingAlerts({
          schemaUpdateMode: this.schemaUpdateMode,
          registerKind: this.registerKind,
          registerName: (this.register && this.register.name) || 'this register',
          groupName: this.facilityGroupName,
          hasFacilityColumn: !!this.facilityColumn,
          showSavedBanner: this.showSavedBanner,
          blockers: this.blockers,
          isExcelSource: this.sourceLabel === 'Excel',
          droppedFacilityColumn: this.droppedFacilityColumn,
          keptFieldCount: this.mappedColumnsCount,
        });
        const identity = this.hasIdentityGuidance && !this.schemaUpdateMode
          ? apiIdentityAlert({
            frequency: this.scheduleFrequency,
            registerKind: this.registerKind,
            configured: this.mappedColumnsCount,
            hasIdentity: this.hasIdentity,
            identityLabel: this.hasIdentity ? this.customFields[this.identityColumn] : '',
            identityColumn: this.identityColumn,
            suggestion: this.identitySuggestion,
          })
          : null;
        return identity ? [...list, identity] : list;
      },
      hasIdentity() {
        return !!this.identityColumn && this.isColumnConfigured(this.identityColumn);
      },
      /** The host (API wizard) passed its schedule: identity guidance follows it. */
      hasIdentityGuidance() {
        return typeof this.scheduleFrequency === 'number';
      },
      /** A configured column unique and non-blank in every preview row ("Use X"); API wizard only. */
      identitySuggestion() {
        if (!this.hasIdentityGuidance || this.schemaUpdateMode) return '';
        const configured = this.filteredExcelColumns.filter((column) => this.isColumnConfigured(column));
        return suggestIdentityColumn(this.excelData, configured, [this.facilityColumn]);
      },
      linkedColumnsCount() {
        return Object.keys(this.regularMappings).filter((column) => this.columnLinks[column]?.linkEntityType).length;
      },
      /** Everything the host wizard needs for its rail summary and footer. */
      mappingStatus() {
        const counts = this.rowEvaluation.counts;
        return {
          canSubmit: this.canSubmit,
          blockers: this.blockers,
          configured: this.mappedColumnsCount,
          totalColumns: this.schemaUpdateMode ? Object.keys(this.customFields).length : this.filteredExcelColumns.length,
          tagColumn: this.selectedTagColumn || '',
          facilityColumn: this.facilityColumn || '',
          facilityColumnState: this.facilityPresentation.stat,
          identity: this.identityColumn && this.isColumnConfigured(this.identityColumn) ? this.customFields[this.identityColumn] : '',
          linked: this.linkedColumnsCount,
          totalRows: counts.total,
          readyRows: counts.ready,
          skippedRows: counts.skipped,
          previewAvailable: this.mappedColumnsCount > 0 && this.registerKind !== REGISTER_KIND.GROUP_UNSET,
          resultingMode: resultingMode(this.registerKind, !!this.facilityColumn),
        };
      },
      configuredFacilityMode() {
        return this.isFacilityGroupImport ? this.register.facilityAssignmentMode : null;
      },
      facilityMappingCompatible() {
        if (!this.isFacilityGroupImport) return true;
        if (this.configuredFacilityMode === 'Shared' && (this.facilityColumn || this.facilityValueMappings.length)) return false;
        if (this.configuredFacilityMode === 'Separated' && !this.facilityColumn) return false;
        if (!this.facilityColumn && this.facilityValueMappings.length) return false;
        if (this.facilityColumn && !this.facilityAliasesValid) return false;
        return true;
      },
      facilityMappingReady() {
        return this.facilityMappingCompatible;
      },
      isFacilityGroupImport() {
        return !this.schemaUpdateMode && !!(this.register && this.register.facilityGroupId && !this.register.facilityId);
      },

      mappedColumnsCount() {
        return Object.keys(this.customFields).filter(
          (column) => this.customFields[column] && this.customFields[column].trim() !== '',
        ).length;
      },

      hasAnyMappings() {
        return this.mappedColumnsCount > 0 || this.selectedTagColumn || this.facilityColumn;
      },

      // Get mandatory field mappings
      mandatoryFieldMappings() {
        const mappings = {};
        Object.keys(this.mandatoryFields).forEach((column) => {
          if (this.mandatoryFields[column] === true && this.customFields[column]) {
            mappings[column] = this.customFields[column];
          }
        });
        return mappings;
      },

      configuredMappings() {
        const mappings = {};
        Object.keys(this.customFields).forEach((column) => {
          if (this.customFields[column] && this.customFields[column].trim() !== '') {
            mappings[column] = this.customFields[column];
          }
        });
        return mappings;
      },

      regularMappings() {
        const mappings = {};
        Object.keys(this.customFields).forEach((column) => {
          const fieldName = this.customFields[column];
          if (
            fieldName &&
            fieldName.trim() !== '' &&
            fieldName !== 'tag' &&
            // exclude facility from regular mappings; it's sent separately via facilityColumn
            fieldName !== 'facilityName'
            // Allow tag column to be mapped as regular field too
          ) {
            mappings[column] = fieldName;
          }
        });
        return mappings;
      },

      regularMappingsCount() {
        return Object.keys(this.regularMappings).length;
      },

      facilityColumnOptions() {
        return this.excelColumns.map((col) => ({
          text: col,
          value: col,
        }));
      },

      // Options for selecting identity field
      identityFieldOptions() {
        const options = [];

        // Add regular field mappings (Excel column name as value, display name as text)
        Object.keys(this.regularMappings || {}).forEach(column => {
          const displayName = this.regularMappings[column];
          if (displayName) {
            options.push({ text: displayName, value: column });
          }
        });

        // Add tag column if selected (Excel column name as value, 'Tag' as text)
        if (this.selectedTagColumn) {
          options.push({ text: 'Tag', value: this.selectedTagColumn });
        }

        return options;
      },


      templateOptions() {
        if (!this.templates || this.templates.length === 0) {
          return [];
        }
        return this.templates.map((t) => ({
          text: t.name,
          value: t.id,
        }));
      },

      ...mapGetters('REGISTERS', {
        templates: 'GET_TEMPLATES',
        templateLoading: 'GET_LOADING',
      }),

      canSaveTemplate() {
        return (
          this.facilityMappingCompatible &&
          this.mappedColumnsCount > 0 &&
          !!this.newTemplateName &&
          this.newTemplateName.trim().length > 0
        );
      },

      // Excel columns filtered to exclude empty headers (defense-in-depth)
      filteredExcelColumns() {
        return this.excelColumns.filter(col => col && String(col).trim() !== '');
      },

      canSubmit() {
        // Require at least one mapping (identity column is optional). A populated Facility Group
        // register without a mode is refused up front (LEG-5) — the server would refuse it anyway.
        return this.mappedColumnsCount > 0
          && !this.isProcessing
          && this.facilityMappingReady
          && this.registerKind !== REGISTER_KIND.GROUP_UNSET;
      },

      /**
       * Whether this mapping step offers the link designation controls. The schema-update dialog
       * always does (it reads the stored designations back); every other surface must opt in.
       */
      linkDesignationEnabled() {
        return this.schemaUpdateMode || this.allowLinkDesignation;
      },
    },

    methods: {
      ...mapActions('REGISTERS', {
        fetchDataTemplates: 'FETCH_DATA_TEMPLATES',
        createDataTemplate: 'CREATE_DATA_TEMPLATE',
        fetchTemplateFieldMapping: 'FETCH_TEMPLATE_FIELD_MAPPING',
        deleteDataTemplate: 'DELETE_DATA_TEMPLATE',
      }),

      // ==========================================
      // MAP FIELDS SECTIONS (accordion)
      // ==========================================

      setSection(key, open) {
        this.sections = { ...this.sections, [key]: !!open };
      },

      toggleSection(key) {
        this.setSection(key, !this.sections[key]);
      },

      /** An alert's "Show": open the section and move focus to its header. */
      showSection(key) {
        this.setSection(key, true);
        this.$nextTick(() => {
          const toggle = key === MAP_SECTION.FACILITY
            ? this.$refs.facilityEditor
            : this.$refs[key === MAP_SECTION.SETTINGS ? 'settingsToggle' : 'columnsToggle'];
          if (!toggle) return;
          if (typeof toggle.focusToggle === 'function') toggle.focusToggle();
          else toggle.focus();
        });
      },

      /**
       * A section that gets a NEW blocking error opens by itself, once. The user may collapse it
       * again; its header then keeps the error visible and the alert offers "Show".
       */
      syncSectionsWithErrors(blockers) {
        const now = errorSections(blockers);
        const fresh = newlyFailingSections(this.lastErrorSections, now);
        if (fresh.length) {
          this.sections = { ...this.sections, ...Object.fromEntries(fresh.map((key) => [key, true])) };
          debugLog('[RegisterImport] section re-opened for a new blocking error', { sections: fresh });
        }
        this.lastErrorSections = now;
      },

      /** A callout action button (identity guidance on an API source). */
      onAlertAction(key) {
        if (key === 'use-identity' && this.identitySuggestion) {
          debugLog('[RegisterApiSource] identity set from the suggestion', { column: this.identitySuggestion });
          this.setIdentity(this.identitySuggestion);
        } else if (key === 'switch-manual') {
          this.$emit('switch-to-manual');
        }
      },

      // Programmatically apply a full mapping into the editor
      applyMapping(incoming = {}) {
        try {
          // A Shared register uses no Facility column: drop it (and its aliases) and keep the rest,
          // with an inline note — instead of refusing the whole mapping.
          const source = incoming || {};
          const droppedAliases = (source.facilityValueMappings || []).length > 0;
          const dropFacility = this.configuredFacilityMode === 'Shared' && (!!source.facilityColumn || droppedAliases);
          const mappingData = dropFacility ? { ...source, facilityColumn: null, facilityValueMappings: [] } : source;
          this.droppedFacilityColumn = dropFacility ? (source.facilityColumn || '') : null;
          if (dropFacility) {
            debugLog('[RegisterImport] Facility column dropped from a mapping loaded into a Shared register', {
              registerId: this.register?.id,
              facilityColumn: source.facilityColumn || null,
              aliases: (source.facilityValueMappings || []).length,
            });
          }
          const { fieldMappings = {}, fieldTypes = {}, mandatoryFields = {}, tagColumn = null, facilityColumn = null, identityColumn = '' } = mappingData;

          // Reset
          this.customFields = {};
          this.fieldNameErrors = {};
          this.mandatoryFields = {};
          this.fieldTypes = {};
          this.selectedTagColumn = null;
          this.facilityColumn = null;
          this.identityColumn = '';

          // Apply
          Object.keys(fieldMappings).forEach((col) => {
            const name = fieldMappings[col];
            this.$set(this.customFields, col, name);
            if (mandatoryFields && Object.values(mandatoryFields).includes(name)) {
              this.$set(this.mandatoryFields, col, true);
            }
            if (fieldTypes && fieldTypes[col] !== undefined) {
              this.$set(this.fieldTypes, col, fieldTypes[col]);
            }
          });

          this.selectedTagColumn = tagColumn || null;
          // A Facility column (and its aliases) only applies where the register can use one
          // (empty or Separated Facility Group register) — never populate a disabled control.
          const facilityUsable = this.schemaUpdateMode || this.facilityColumnEnabled;
          this.facilityColumn = facilityUsable ? (facilityColumn || null) : null;
          this.facilityValueMappings = facilityUsable && this.facilityColumn
            ? copyFacilityAliases(mappingData.facilityValueMappings)
            : [];
          this.identityColumn = identityColumn || '';

          this.emitMappingComplete();
          this.$dialog?.notify?.success && this.$dialog.notify.success('Schema loaded');
        } catch (e) {
          debugError('Failed to apply mapping:', e);
          this.$dialog?.notify?.error && this.$dialog.notify.error('Failed to load saved schema');
        }
      },

      // ==========================================
      // LINK DESIGNATION ("Links to")
      // ==========================================

      /**
       * Load the register's stored column definitions and hydrate the link designations from them.
       * Read through GET api/register-data/{id}/schema rather than the `register` prop: that
       * endpoint's stored-schema branch is the verified carrier of the four link fields AND of the
       * internal `name` this feature writes back with.
       */
      async loadColumnLinks() {
        if (!this.linkDesignationEnabled) return;

        // Import: the register has no stored schema yet — the server creates it (and assigns the
        // internal column names) while the import itself runs — so there is nothing to read back.
        // Seed blank designations off the mapped columns instead.
        if (!this.schemaUpdateMode) {
          this.syncImportColumnLinks();
          return;
        }

        if (!this.register || !this.register.id) return;

        let columns = null;
        try {
          const schema = await registerService.getRegisterSchema(this.register.id);
          columns = Array.isArray(schema?.columns) ? schema.columns : null;
        } catch (err) {
          debugWarn('[RegisterLink] schema read failed; falling back to the register prop', err);
        }

        if (!columns) {
          columns = Array.isArray(this.register.schema) ? this.register.schema : [];
        }
        this.hydrateColumnLinks(columns);
      },

      /**
       * Build the designation state from stored column definitions. Keyed exactly like
       * customFields (originalName first) so the register-fields list can look an entry up by its
       * own v-for key.
       * @param {Array<object>} columns - RegisterColumnVM entries
       */
      hydrateColumnLinks(columns) {
        const links = {};
        (columns || []).forEach((col) => {
          if (!col || col.isSystemColumn || !col.name) return;
          const fieldKey = col.originalName || col.displayName || col.name;
          if (!fieldKey) return;
          links[fieldKey] = {
            name: col.name, // internal name — the identity the link endpoint matches on
            // The display name AS STORED when this was hydrated — deliberately NOT kept in step
            // with the field-name box. It is the SERVER's SourceColumn for this column (which
            // falls back to the internal name when a column carries no display name), and the
            // server hashes SourceColumn into the register's resolution config — so renaming a
            // DESIGNATED column invalidates its stored links. The baseline copy below is what
            // ExcelUpload diffs the saved names against to detect exactly that.
            storedDisplayName: col.displayName || col.name || null,
            linkEntityType: col.linkEntityType || null,
            linkKeyField: col.linkKeyField || null,
            isDefaultHighlight: !!col.isDefaultHighlight,
            isDefaultContext: !!col.isDefaultContext,
          };
        });

        this.columnLinks = links;
        this.columnLinksBaseline = JSON.parse(JSON.stringify(links));
        debugLog('[RegisterLink] designations hydrated', {
          registerId: this.register?.id,
          columns: Object.keys(links).length,
          designated: Object.keys(links).filter((key) => links[key].linkEntityType),
        });
      },

      /**
       * Keep one designation entry per MAPPED column while importing, so the "Links to" controls
       * render on the very first import instead of only in the schema-update dialog.
       *
       * The entries deliberately carry `name: null`. The link endpoint matches a column by its
       * INTERNAL name, and the server only assigns that name while the import runs — so it can be
       * learned only afterwards, by re-reading the created schema
       * (see ExcelUpload.applyImportColumnLinks). Guessing it here is the `field_1` trap.
       *
       * A blank entry whose column is no longer mapped is dropped; a DESIGNATED one is kept even
       * while its field name is momentarily empty (mid-rename), so a keystroke cannot silently
       * discard the user's choice. `collectImportColumnLinkDesignations` emits only the columns
       * that are actually mapped at save time.
       */
      syncImportColumnLinks() {
        const mappedColumns = Object.keys(this.regularMappings);
        const seeded = [];

        mappedColumns.forEach((column) => {
          if (this.columnLinks[column]) return;
          this.$set(this.columnLinks, column, {
            name: null, // assigned by the server during the import; resolved afterwards
            storedDisplayName: null, // nothing stored yet — the import creates the column
            linkEntityType: null,
            linkKeyField: null,
            isDefaultHighlight: false,
            isDefaultContext: false,
          });
          seeded.push(column);
        });

        const dropped = [];
        Object.keys(this.columnLinks).forEach((column) => {
          if (mappedColumns.includes(column)) return;
          if (this.columnLinks[column]?.linkEntityType) return;
          this.$delete(this.columnLinks, column);
          dropped.push(column);
        });

        if (seeded.length || dropped.length) {
          debugLog('[RegisterLink] import designations synced', {
            mapped: mappedColumns.length,
            seeded,
            dropped,
            designated: Object.keys(this.columnLinks).filter(
              (key) => this.columnLinks[key].linkEntityType,
            ),
          });
        }
      },

      /**
       * The designation entry for a register-fields row, or null when the column carries none —
       * a column missing from the stored schema, or a surface that has not opted into the
       * designation controls at all (the API data-source wizard).
       * @param {string} column
       * @returns {object|null}
       */
      columnLink(column) {
        return this.columnLinks[column] || null;
      },

      /** Key-field choices for a column's declared type (only fields its resolver can match on). */
      linkKeyFieldOptions(column) {
        return keyFieldOptions(this.columnLink(column)?.linkEntityType);
      },

      /** Register-default choices, with Highlight disabled + explained for types that can't narrow. */
      linkDefaultRoleOptions(column) {
        return defaultRoleOptions(this.columnLink(column)?.linkEntityType);
      },

      /** The single role the picker shows, collapsed from the two stored flags. */
      linkDefaultRole(column) {
        return roleFromFlags(this.columnLink(column));
      },

      /**
       * Replace a column's designation with a new object (never mutate in place) so Vue tracks it.
       * @param {string} column
       * @param {object} patch - fields to override
       * @returns {object|null} the new entry
       */
      setColumnLink(column, patch) {
        const link = this.columnLinks[column];
        if (!link) return null;
        const next = { ...link, ...patch };
        this.$set(this.columnLinks, column, next);
        return next;
      },

      /**
       * Type changed. A key field and a register default are both meaningless without a type, and a
       * key field belonging to the previous type may be unresolvable under the new one — so reset
       * the key field to the new type's default rather than carrying a stale value over.
       */
      onLinkEntityTypeChange(column, value) {
        const entityType = value || null;
        const link = this.columnLinks[column];
        if (!link) return;

        const patch = {
          linkEntityType: entityType,
          linkKeyField: defaultKeyFieldFor(entityType),
          // A type that covers the whole asset (or has no geometry) cannot be the highlight, and a
          // type with no geometry cannot be the context either — drop a default the new type can
          // no longer play rather than carry an unplayable one over.
          isDefaultHighlight: link.isDefaultHighlight && canHighlight(entityType),
          isDefaultContext: link.isDefaultContext && canContext(entityType),
        };
        this.setColumnLink(column, patch);
        debugLog('[RegisterLink] entity type changed', {
          column,
          columnName: link.name,
          entityType,
          keyField: patch.linkKeyField,
        });
        this.emitMappingComplete();
      },

      /** Key field changed. Clearing the select falls back to the type's default. */
      onLinkKeyFieldChange(column, value) {
        const link = this.columnLinks[column];
        if (!link) return;
        const keyField = value || defaultKeyFieldFor(link.linkEntityType);
        this.setColumnLink(column, { linkKeyField: keyField });
        debugLog('[RegisterLink] key field changed', { column, columnName: link.name, keyField });
        this.emitMappingComplete();
      },

      /**
       * Register default changed. At most one column per register may carry each default, so the
       * flag is cleared on every other column here — mirroring what the server does on save, so the
       * dialog never shows two defaults of the same kind.
       */
      onLinkDefaultRoleChange(column, role) {
        const link = this.columnLinks[column];
        if (!link) return;

        const flags = flagsFromRole(role);
        Object.keys(this.columnLinks).forEach((other) => {
          if (other === column) return;
          const otherLink = this.columnLinks[other];
          if (flags.isDefaultHighlight && otherLink.isDefaultHighlight) {
            this.setColumnLink(other, { isDefaultHighlight: false });
          }
          if (flags.isDefaultContext && otherLink.isDefaultContext) {
            this.setColumnLink(other, { isDefaultContext: false });
          }
        });

        this.setColumnLink(column, flags);
        debugLog('[RegisterLink] register default changed', { column, columnName: link.name, role });
        this.emitMappingComplete();
      },

      /**
       * The designations that differ from what is stored — one entry per column the save must PUT.
       * `columnName` is the INTERNAL column name; the link endpoint matches on it, NOT on the
       * displayName (which is the physical SQL-view column the dashboard query uses).
       * @returns {Array<object>}
       */
      collectColumnLinkChanges() {
        const changes = [];
        Object.keys(this.columnLinks).forEach((column) => {
          const current = this.columnLinks[column];
          if (!current || !current.name) return;

          const baseline = this.columnLinksBaseline[column];
          const unchanged =
            baseline &&
            (baseline.linkEntityType || null) === (current.linkEntityType || null) &&
            (baseline.linkKeyField || null) === (current.linkKeyField || null) &&
            !!baseline.isDefaultHighlight === !!current.isDefaultHighlight &&
            !!baseline.isDefaultContext === !!current.isDefaultContext;
          if (unchanged) return;

          changes.push({
            columnName: current.name,
            fieldKey: column,
            linkEntityType: current.linkEntityType || null,
            linkKeyField: current.linkKeyField || null,
            isDefaultHighlight: !!current.isDefaultHighlight,
            isDefaultContext: !!current.isDefaultContext,
          });
        });
        return changes;
      },

      /**
       * The designations to write after a FIRST import, one entry per DESIGNATED column that is
       * still mapped. Deliberately carries NO `columnName`: the internal name the link endpoint
       * matches on does not exist until the server builds the schema during the import, so the
       * caller fills it in from the created schema (ExcelUpload.applyImportColumnLinks).
       *
       * `fieldKey` (the source column) and `displayName` (the field name the user assigned) are
       * BOTH carried, because they are the two identities the stored schema records — a fresh
       * column keeps the source column as its originalName, while a re-import merges onto an
       * existing column and keeps that column's own originalName instead.
       *
       * Only designated columns are emitted — a register being imported for the first time has
       * nothing stored to clear, so a "None" column would be a no-op PUT.
       * @returns {Array<object>}
       */
      collectImportColumnLinkDesignations() {
        const designations = [];
        Object.keys(this.regularMappings).forEach((column) => {
          const current = this.columnLinks[column];
          if (!current || !current.linkEntityType) return;

          designations.push({
            fieldKey: column,
            displayName: this.regularMappings[column],
            linkEntityType: current.linkEntityType,
            linkKeyField: current.linkKeyField || null,
            isDefaultHighlight: !!current.isDefaultHighlight,
            isDefaultContext: !!current.isDefaultContext,
          });
        });
        return designations;
      },

      // Check if a column is configured with a field name
      isColumnConfigured(column) {
        return this.customFields[column] && this.customFields[column].trim() !== '';
      },

      // Check if field name is unique
      isFieldNameUnique(value) {
        if (!value) return true;
        const usedNames = Object.values(this.customFields).filter(
          (name) => name && name.trim() !== '',
        );
        return usedNames.filter((name) => name.toLowerCase() === value.toLowerCase()).length <= 1;
      },

      // Check if field name is valid format
      isValidFieldName(value) {
        if (!value) return true;
        // Allow letters, numbers, underscores, and hyphens, must start with letter or underscore
        const validPattern = /^[a-zA-Z_][a-zA-Z0-9_-]*$/;
        return validPattern.test(value);
      },

      // Handle field name change
      onFieldNameChange(column, value) {
        this.customFields[column] = value;
        if (!this.fieldTypes[column]) {
          this.$set(this.fieldTypes, column, this.detectFieldType(column));
        }
        this.validateFieldName(column, value);
        this.emitMappingComplete();
      },

      // Clear field name for a column
      clearFieldName(column) {
        this.$set(this.customFields, column, '');
        delete this.fieldNameErrors[column];
        this.$delete(this.mandatoryFields, column); // Also clear mandatory flag
        // An unmapped column cannot be the identity.
        if (this.identityColumn === column) this.identityColumn = '';
        this.emitMappingComplete();
      },

      setMandatory(column, checked) {
        this.$set(this.mandatoryFields, column, !!checked);
        this.onMandatoryChange(column);
      },

      setIdentity(column) {
        this.identityColumn = column;
        this.emitMappingComplete();
      },

      loadSavedSchema() {
        this.savedSchemaHandled = true;
        const saved = this.savedSchemaMapping;
        if (!saved) return;
        // The saved schema names its identity by FIELD name (display name), while this step keys
        // identity by SOURCE column — translate it, or the identity would never pre-select.
        const fieldMappings = saved.fieldMappings || {};
        const identity = saved.identityColumn;
        const identityColumn = identity && !(identity in fieldMappings)
          ? Object.keys(fieldMappings).find((column) => fieldMappings[column] === identity) || identity
          : identity;
        this.applyMapping({ ...saved, identityColumn });
      },

      dismissSavedSchema() {
        this.savedSchemaHandled = true;
      },

      /** Member Facility name a raw Facility value maps to through an alias (preview "→ name"). */
      aliasTargetOf(raw) {
        if (!this.facilityApplies) return null;
        const match = resolveFacilityValue(raw, this.facilityValueMappings, this.members);
        return match.kind === FACILITY_MATCH.ALIAS ? match.facilityName : null;
      },

      isSpecialColumn(column) {
        return !this.schemaUpdateMode && (column === this.selectedTagColumn || column === this.facilityColumn);
      },

      rowClass(column) {
        const on = this.isColumnConfigured(column);
        return {
          'is-configured': on,
          'is-special': !on && this.isSpecialColumn(column),
          'has-link': on && !!(this.columnLinks[column] && this.columnLinks[column].linkEntityType),
        };
      },

      rowStatusIcon(column) {
        if (this.isColumnConfigured(column)) return 'mdi-check-circle';
        return this.isSpecialColumn(column) ? 'mdi-star-circle-outline' : 'mdi-circle-outline';
      },

      fieldPlaceholder(column) {
        if (!this.schemaUpdateMode && column === this.selectedTagColumn) return 'Used as Tag — optional';
        if (!this.schemaUpdateMode && column === this.facilityColumn) return 'Used as Facility — optional';
        return column;
      },

      linkTypeLabel(column) {
        const type = this.columnLinks[column] && this.columnLinks[column].linkEntityType;
        const option = LINK_ENTITY_TYPE_OPTIONS.find((o) => o.value === type);
        return option ? option.text : type;
      },

      /**
       * Current member Facilities of the Facility Group. Loaded here (not in the alias editor) so the
       * editor and the preview resolve Facility values against the same list.
       */
      async loadMembers() {
        const generation = ++this.membersGeneration;
        this.members = [];
        this.membersError = '';
        if (!this.isFacilityGroupImport) {
          this.membersLoading = false;
          return;
        }
        const groupId = this.register.facilityGroupId;
        this.membersLoading = true;
        try {
          const response = await groupsService.getById(groupId);
          if (generation !== this.membersGeneration) return;
          this.members = (response.data?.facilities || []).filter((member) => member.facilityGroupId === groupId);
          debugLog('[RegisterImport] Facility Group members loaded', { groupId, members: this.members.length });
        } catch (err) {
          if (generation !== this.membersGeneration) return;
          debugWarn('[RegisterImport] Facility Group members could not be loaded', { groupId, err });
          this.membersError = 'The Facility Group’s Facilities could not be loaded. Close and reopen the import to try again.';
        } finally {
          if (generation === this.membersGeneration) this.membersLoading = false;
        }
      },

      // Handle mandatory field change
      onMandatoryChange() {
        this.emitMappingComplete();
      },

      // Handle field type change
      onFieldTypeChange() {
        this.emitMappingComplete();
      },

      // Validate field name for a specific column
      validateFieldName(column, value) {
        const errors = [];

        if (!value || value.trim() === '') {
          errors.push('Field name is required');
        } else {
          if (!this.isValidFieldName(value)) {
            errors.push(
              'Field name must start with letter or underscore and contain only letters, numbers, underscores, and hyphens',
            );
          }
          if (!this.isFieldNameUnique(value)) {
            errors.push('Field name must be unique');
          }
        }

        if (errors.length > 0) {
          this.fieldNameErrors[column] = errors;
        } else {
          delete this.fieldNameErrors[column];
        }
      },

      // Auto-suggest field names based on column names (default to same as column name)
      autoSuggestFieldNames() {
        if (!this.excelColumns || this.excelColumns.length === 0) {
          this.$dialog.notify.warning('No Excel columns available for auto-fill');
          return;
        }

        this.excelColumns.forEach((column) => {
          if (!this.customFields[column] || this.customFields[column].trim() === '') {
            // Default to the same name as the column
            const suggestedName = column //this.suggestFieldName(column);
            this.$set(this.customFields, column, suggestedName);
            if (!this.fieldTypes[column]) {
              this.$set(this.fieldTypes, column, this.detectFieldType(column));
            }
            this.validateFieldName(column, suggestedName);
          }
        });

        this.emitMappingComplete();
      },

      // Suggest a field name based on column name (default to same name)
      suggestFieldName(columnName) {
        // Default to the same name as the column, but make it valid
        let fieldName = columnName
          .replace(/[^a-zA-Z0-9\s_-]/g, '') // Remove special characters except underscore and hyphen
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .replace(/^[0-9]/, '_$&'); // Add underscore if starts with number

        // Ensure it starts with letter or underscore
        if (!/^[a-zA-Z_]/.test(fieldName)) {
          fieldName = 'field_' + fieldName;
        }

        // Make it unique
        let uniqueName = fieldName;
        let counter = 1;
        while (!this.isFieldNameUnique(uniqueName)) {
          uniqueName = fieldName + '_' + counter;
          counter++;
        }

        return uniqueName;
      },

      // Reset all field names
      resetFieldNames() {
        if (!this.hasAnyMappings) {
          return;
        }

        this.customFields = {};
        this.facilityValueMappings = [];
        this.fieldNameErrors = {};
        this.mandatoryFields = {}; // Also clear mandatory fields
        this.fieldTypes = {}; // Clear field types
        this.selectedTagColumn = null;
        this.facilityColumn = null;
        this.identityColumn = '';
        this.droppedFacilityColumn = null;
        this.emitMappingComplete();
      },

      // Get column samples for preview
      getColumnSamples(column) {
        if (!this.excelData || this.excelData.length === 0) return [];

        const samples = this.excelData
          .slice(0, 3)
          .map((row) => row[column])
          .filter((value) => value !== null && value !== undefined && value !== '');

        return samples;
      },

      // Get column preview text
      getColumnPreview(column) {
        const samples = this.getColumnSamples(column);
        if (samples.length > 0) {
          return samples.slice(0, 3).join(', ');
        }
        return 'No preview available';
      },

      // Emit mapping complete event
      emitMappingComplete() {
        // While importing there is no stored schema to hydrate designations from, so they track
        // the columns the user has mapped so far. Synced here — the one call every mapping change
        // funnels through — rather than from a watcher on `regularMappings`: a user watcher on a
        // computed flushes on the next tick, which would build this payload from stale state.
        if (this.linkDesignationEnabled && !this.schemaUpdateMode) {
          this.syncImportColumnLinks();
        }

        const mappings = {};
        Object.keys(this.customFields).forEach((column) => {
          // Skip entries with empty column keys or empty field names
          if (!column || column.trim() === '') return;
          if (this.customFields[column] && this.customFields[column].trim() !== '') {
            mappings[column] = this.customFields[column];
          }
        });

        // Build field types mapping using FIELD_TYPES enum values
        const fieldTypeMappings = {};
        Object.keys(this.fieldTypes).forEach((column) => {
          if (this.customFields[column] && this.customFields[column].trim() !== '') {
            // Ensure we use the numeric enum values
            fieldTypeMappings[column] = this.fieldTypes[column] || 1; // Default to TEXT
          }
        });

        // Include special column selections and mandatory fields
        const mappingData = {
          fieldMappings: mappings,
          mandatoryFields: this.mandatoryFieldMappings,
          fieldTypes: fieldTypeMappings,
          tagColumn: this.selectedTagColumn,
          // Independent source column for optional row facility context, not a mapped field.
          facilityColumn: this.facilityColumn,
          facilityValueMappings: copyFacilityAliases(this.facilityValueMappings),
          identityColumn: this.identityColumn || null,
        };

        // Link designations. Carried in the payload (rather than read off this component at save
        // time) because the parent unmounts the mapping step as soon as the save succeeds.
        //
        // Two shapes, one key: schema-update sends only what CHANGED, already keyed by internal
        // column name; import sends every designation keyed by `fieldKey`, because the internal
        // name does not exist yet. Both are consumed by ExcelUpload only — `registerService
        // .importData` rebuilds `mapping` from an explicit whitelist, so nothing extra reaches the
        // import endpoint. Surfaces that forward this object verbatim (the API data-source wizard)
        // do not opt in, so their payload is unchanged.
        if (this.linkDesignationEnabled) {
          mappingData.columnLinks = this.schemaUpdateMode
            ? this.collectColumnLinkChanges()
            : this.collectImportColumnLinkDesignations();
        }

        this.$emit('mapping-complete', mappingData);
      },

      async onTemplateSelected(templateId) {
        if (!templateId) {
          this.selectedTemplateData = null;
          return;
        }

        try {
          // Fetch the template data for validation (but don't apply it yet)
          debugLog('Available templates:', this.templates);
          debugLog('Looking for template ID:', templateId);

          const template = this.templates.find(t => t.id === templateId);

          if (!template) {
            debugError('Template not found with ID:', templateId);
            debugError('Available template IDs:', this.templates.map(t => t.id));
            this.$dialog.notify.error('Template not found');
            this.selectedTemplateData = null;
            return;
          }

          debugLog('Selected template:', template); // Debug logging
          debugLog('Template structure:', JSON.stringify(template, null, 2)); // More detailed logging
          this.selectedTemplateData = template;
        } catch (error) {
          debugError('Failed to load template:', error);
          this.$dialog.notify.error('Failed to load template');
          this.selectedTemplateData = null;
        }
      },

      async saveCurrentTemplate() {
        if (!this.canSaveTemplate) return;

        try {
          const templateData = {
            name: this.newTemplateName.trim(),
            template: {
              fieldMappings: this.configuredMappings,
              mandatoryFields: this.mandatoryFieldMappings,
              fieldTypes: this.getFieldTypeMappings(),
              tagColumn: this.selectedTagColumn,
              facilityColumn: this.facilityColumn,
              facilityValueMappings: copyFacilityAliases(this.facilityValueMappings),
              identityColumn: this.identityColumn || null,
            }
          };

          const createdTemplate = await this.createDataTemplate(templateData);
          this.newTemplateName = '';
          this.selectedTemplateId = createdTemplate.id;
          this.$dialog.notify.success('Template saved successfully');
        } catch (error) {
          debugError('Failed to save template:', error);
          this.$dialog.notify.error('Failed to save template');
        }
      },

      getFieldTypeMappings() {
        const fieldTypeMappings = {};
        Object.keys(this.fieldTypes).forEach((column) => {
          if (this.customFields[column] && this.customFields[column].trim() !== '') {
            fieldTypeMappings[column] = this.fieldTypes[column] || 1;
          }
        });
        return fieldTypeMappings;
      },
      async loadSelectedTemplate() {
        if (!this.selectedTemplateId || !this.selectedTemplateData) return;

        try {
          // Create filtered mapping with only fields that exist in current Excel
          debugLog('selectedTemplateData:', this.selectedTemplateData); // Debug

          // Handle different possible template structures
          let template = null;

          if (Object.prototype.hasOwnProperty.call(this.selectedTemplateData, 'template')) {
            // Standard API structure: { id, name, template: {...} }
            template = this.selectedTemplateData.template;

            // Handle case where template property is null - fetch it separately
            if (template === null) {
              debugLog('Template is null, fetching field mapping separately...');
              try {
                template = await this.fetchTemplateFieldMapping(this.selectedTemplateId);
              } catch (error) {
                debugError('Failed to fetch template field mapping:', error);
                this.$dialog.notify.error('Failed to load template mapping');
                return;
              }
            }
          } else if (this.selectedTemplateData.fieldMappings) {
            // Flat structure: { id, name, fieldMappings, fieldTypes, ... }
            template = this.selectedTemplateData;
          } else {
            debugError('selectedTemplateData has no valid template structure');
            debugError('selectedTemplateData keys:', Object.keys(this.selectedTemplateData));
            this.$dialog.notify.error('Template data is invalid');
            return;
          }
          const filteredMapping = this.createFilteredMapping(template);

          if (!filteredMapping) {
            this.$dialog.notify.error('Failed to process template data');
            return;
          }

          // Apply the filtered mapping
          this.applyMapping(filteredMapping);
        } catch (error) {
          debugError('Failed to apply template:', error);
          this.$dialog.notify.error('Failed to apply template');
        }
      },

      async deleteTemplate(templateId, templateName) {
        if (!templateId) return;

        // Show confirmation dialog
        const confirmed = await this.showDeleteConfirmation(templateName);
        if (!confirmed) return;

        try {
          await this.deleteDataTemplate(templateId);

          // Clear selection if deleted template was selected
          if (this.selectedTemplateId === templateId) {
            this.selectedTemplateId = null;
            this.selectedTemplateData = null;
          }

          // Refresh templates list
          await this.fetchDataTemplates();

          this.$dialog.notify.success(`Template "${templateName}" deleted successfully`);
        } catch (error) {
          debugError('Failed to delete template:', error);
          this.$dialog.notify.error('Failed to delete template');
        }
      },

      showDeleteConfirmation(templateName) {
        return new Promise((resolve) => {
          const withAliases = this.isFacilityGroupImport && this.facilityColumnEnabled ? ', with its Facility name mapping' : '';
          this.$dialog.error({
            title: 'Delete template?',
            text: `"${templateName}"${withAliases} will be removed from the template list. This can’t be undone.`,
            actions: {
              false: 'Cancel',
              true: {
                text: 'Delete',
                color: 'error',
                handle: () => resolve(true)
              }
            }
          }).catch(() => resolve(false));
        });
      },

      createFilteredMapping(template) {
        if (!template) {
          debugError('Template is null or undefined');
          return null;
        }

        debugLog('Processing template in createFilteredMapping:', template);
        debugLog('Template type:', typeof template);
        debugLog('Template keys:', Object.keys(template));

        const availableColumns = this.excelColumns || [];
        const filteredMapping = {
          fieldMappings: {},
          mandatoryFields: {},
          fieldTypes: {},
          tagColumn: null,
          facilityColumn: null,
          facilityValueMappings: copyFacilityAliases(template.facilityValueMappings),
          identityColumn: template.identityColumn || ''
        };

        // Filter field mappings to only include columns that exist
        Object.keys(template.fieldMappings || {}).forEach(excelColumn => {
          if (availableColumns.includes(excelColumn)) {
            filteredMapping.fieldMappings[excelColumn] = template.fieldMappings[excelColumn];

            // Copy corresponding field types
            if (template.fieldTypes && template.fieldTypes[excelColumn]) {
              filteredMapping.fieldTypes[excelColumn] = template.fieldTypes[excelColumn];
            }

            // Copy mandatory field status
            if (template.mandatoryFields && template.mandatoryFields[excelColumn]) {
              filteredMapping.mandatoryFields[excelColumn] = template.mandatoryFields[excelColumn];
            }
          }
        });

        // Set special columns only if they exist in current Excel
        if (template.tagColumn && availableColumns.includes(template.tagColumn)) {
          filteredMapping.tagColumn = template.tagColumn;
        }

        if (template.facilityColumn && availableColumns.includes(template.facilityColumn)) {
          filteredMapping.facilityColumn = template.facilityColumn;
        }

        return filteredMapping;
      },

      // Save mappings and proceed to import
      saveMappings() {
        if (!this.canSubmit) return;
        if (this.mappedColumnsCount === 0) {
          this.$dialog.notify.warning('Please configure field names first');
          return;
        }

        this.isProcessing = true;

        // Emit the current source mappings before import.
        this.emitMappingComplete();

        this.$emit('proceed-to-import');
      },

      // Reset processing state (called by parent when import fails)
      resetProcessingState() {
        this.isProcessing = false;
      },

      // Handle tag column selection change
      onTagColumnChange() {
        // Clear any existing 'tag' mapping from other columns
        Object.keys(this.customFields).forEach((column) => {
          if (column !== this.selectedTagColumn && (this.customFields[column] === 'tag' || this.customFields[column] === 'name')) {
            this.customFields[column] = '';
          }
        });

        // Set field type for tag column if not already set
        if (this.selectedTagColumn && !this.fieldTypes[this.selectedTagColumn]) {
          this.$set(this.fieldTypes, this.selectedTagColumn, FIELD_TYPES.TEXT);
        }

        // Note: We don't automatically set customFields[selectedTagColumn] = 'tag' anymore
        // This allows users to map the tag column to their own field name
        // The special "Tag" field display is handled separately in the template

        this.emitMappingComplete();
      },

      // Keep facility context selection independent from ordinary field mappings.
      onFacilityColumnChange() {
        if (!this.facilityColumn) this.facilityValueMappings = [];
        this.emitMappingComplete();
      },

      // Clear tag column selection
      clearTagColumn() {
        this.selectedTagColumn = null;
        // Only clear 'tag' or 'name' mappings, but preserve custom field mappings
        Object.keys(this.customFields).forEach((column) => {
          if (this.customFields[column] === 'tag' || this.customFields[column] === 'name') {
            this.customFields[column] = '';
          }
        });
        if (this.identityColumn === this.selectedTagColumn) this.identityColumn = '';
        this.emitMappingComplete();
      },

      onIdentityFieldChange() {
        this.emitMappingComplete();
      },

      onIdentityCheckboxChange(columnName) {
        // Assign identityColumn to the selected Excel column name; toggling off clears it
        if (this.identityColumn === columnName) {
          this.identityColumn = columnName;
        } else if (!this.identityColumn) {
          this.identityColumn = '';
        }
        this.emitMappingComplete();
      },

      // Initialize schema fields from register schema for schema update mode
      initializeSchemaFields() {
        if (!this.schemaUpdateMode || !this.register || !this.register.schema) {
          return;
        }

        try {
          // Create field entries from existing schema
          const schema = this.register.schema;
          if (Array.isArray(schema)) {
            schema.forEach((field) => {
              if (field.displayName || field.name) {
                const fieldName = field.displayName || field.name;

                // Exclude system fields from editing
                if (this.isSystemField(fieldName)) {
                  return;
                }

                const fieldKey = field.originalName || field.displayName || field.name;
                this.$set(this.customFields, fieldKey, fieldName);

                // Set field type if available
                if (field.dataType) {
                  const typeEnum = this.mapDataTypeToEnum(field.dataType);
                  this.$set(this.fieldTypes, fieldKey, typeEnum);
                }

                // Set mandatory flag if available
                if (field.isMandatory) {
                  this.$set(this.mandatoryFields, fieldKey, true);
                }

                // Set identity column if this is the identity field
                if (field.isIdentityColumn) {
                  this.identityColumn = fieldKey;
                }
              }
            });
          }

          // Emit initial mapping complete
          this.emitMappingComplete();
        } catch (error) {
          debugError('Failed to initialize schema fields:', error);
        }
      },

      // Check if field is a system field that should not be editable
      isSystemField(fieldName) {
        const systemFields = ['Row #', 'Tag', 'Linked Tags', 'TagValue', 'FacilityName'];
        return systemFields.includes(fieldName);
      },

      // Map data type string to enum value
      mapDataTypeToEnum(dataType) {
        const typeString = String(dataType || '').toLowerCase();
        if (typeString === 'number') return FIELD_TYPES.NUMBER;
        if (typeString === 'date') return FIELD_TYPES.DATE;
        if (typeString === 'boolean') return FIELD_TYPES.BOOLEAN;
        return FIELD_TYPES.TEXT;
      },

      // Auto-detect field type by sampling values from excelData
      detectFieldType(column) {
        if (!this.excelData || this.excelData.length === 0) return FIELD_TYPES.TEXT;

        // Sample up to 20 non-empty values
        const samples = [];
        for (let i = 0; i < this.excelData.length && samples.length < 20; i++) {
          const val = this.excelData[i][column];
          if (val !== null && val !== undefined && String(val).trim() !== '') {
            samples.push(val);
          }
        }

        if (samples.length === 0) return FIELD_TYPES.TEXT;

        const counts = { number: 0, date: 0, boolean: 0, text: 0 };

        for (const val of samples) {
          // Native boolean
          if (typeof val === 'boolean') { counts.boolean++; continue; }
          // Native number
          if (typeof val === 'number' && !isNaN(val)) { counts.number++; continue; }
          // Native Date object
          if (val instanceof Date && !isNaN(val.getTime())) { counts.date++; continue; }

          const str = String(val).trim();

          // Boolean-like strings
          if (/^(true|false|yes|no|0|1)$/i.test(str)) { counts.boolean++; continue; }

          // Numeric strings (integers and decimals, with optional sign)
          if (/^-?\d{1,15}(\.\d+)?$/.test(str) && !isNaN(Number(str))) { counts.number++; continue; }

          // ISO datetime: 2024-01-15T10:30:00
          if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) { counts.date++; continue; }
          // Date only: 2024-01-15 or 2024/01/15
          if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(str)) { counts.date++; continue; }
          // Common date: dd/mm/yyyy, mm-dd-yyyy, dd.mm.yyyy
          if (/^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/.test(str) && !isNaN(Date.parse(str))) { counts.date++; continue; }

          counts.text++;
        }

        // A type wins if >= 70% of samples match it
        const threshold = samples.length * 0.7;
        if (counts.boolean >= threshold) return FIELD_TYPES.BOOLEAN;
        if (counts.date >= threshold) return FIELD_TYPES.DATE;
        if (counts.number >= threshold) return FIELD_TYPES.NUMBER;
        return FIELD_TYPES.TEXT;
      },
    },
    watch: {
      mappingStatus: { immediate: true, handler(status) { this.$emit('status', status); } },
      blockers: { immediate: true, handler(list) { this.syncSectionsWithErrors(list); } },
      // The register's row count can arrive after a mapping was applied: once the Facility column
      // stops being usable (e.g. the register turns out to be populated), drop it and its aliases.
      facilityColumnEnabled(enabled) {
        if (enabled || this.schemaUpdateMode || (!this.facilityColumn && !this.facilityValueMappings.length)) return;
        this.facilityColumn = null;
        this.facilityValueMappings = [];
        this.emitMappingComplete();
      },
      'register.facilityGroupId': { immediate: true, handler() { this.loadMembers(); } },
      facilityMappingReady: { immediate: true, handler(value) { this.$emit('mapping-validity', value && !!(this.hasAnyMappings || this.identityColumn)); } },
      hasAnyMappings(value) { this.$emit('mapping-validity', this.facilityMappingReady && !!(value || this.identityColumn)); },
      identityColumn() { this.$emit('mapping-validity', this.facilityMappingReady && !!(this.hasAnyMappings || this.identityColumn)); },
    },
    async created() {
      // Load available templates
      try {
        await this.fetchDataTemplates();
        debugLog('Loaded templates:', this.templates); // Debug logging
        debugLog('Templates structure:', JSON.stringify(this.templates, null, 2)); // More detailed logging
      } catch (error) {
        debugError('Failed to load templates:', error);
      }

      // Initialize schema fields for schema update mode
      if (this.schemaUpdateMode && this.register && this.register.schema) {
        this.initializeSchemaFields();
      }

      // Link designations are read from the stored schema, not the register prop
      await this.loadColumnLinks();
    },

    mounted() {
      if (this.initialMapping && !this.schemaUpdateMode) this.applyMapping(this.initialMapping);
      // Initialize schema fields after component is mounted (for reactivity)
      if (this.schemaUpdateMode && this.register && this.register.schema) {
        this.initializeSchemaFields();
      }
    },
  };
</script>
